import React, { useMemo } from 'react';
import { Button } from '../../../ui';
import { formatDateTime } from '../../../../utils/dateFormatter';
import { SIGNATURE_STATUS } from './signatureStatusConstants';

const DEFAULT_NOT_SIGNABLE_MESSAGE = 'La solicitud no está disponible para firma en este momento.';

const STATUS_TONE_CLASS = {
  info: 'rg-signature-status-card--info',
  pending: 'rg-signature-status-card--pending',
  success: 'rg-signature-status-card--success',
  warning: 'rg-signature-status-card--warning',
  error: 'rg-signature-status-card--error'
};

const resolveStatusContent = (signatureStatus, apiMessage) => {
  switch (signatureStatus) {
    case SIGNATURE_STATUS.GENERATING:
      return {
        title: 'Generando enlace de firma',
        description: 'Estamos preparando el acceso para el cliente.',
        tone: 'info',
        showSpinner: true,
        showRefresh: false
      };
    case SIGNATURE_STATUS.PENDING:
      return {
        title: 'Esperando la firma del cliente',
        description: '',
        tone: 'pending',
        showSpinner: false,
        showRefresh: true
      };
    case SIGNATURE_STATUS.COMPLETED:
      return {
        title: 'Firma validada',
        description: 'La firma del cliente fue recibida correctamente. Ya podés continuar a la confirmación.',
        tone: 'success',
        showSpinner: false,
        showRefresh: false
      };
    case SIGNATURE_STATUS.EXPIRED:
      return {
        title: 'El enlace de firma venció',
        description: 'El cliente ya no puede utilizar este enlace. Generá uno nuevo para continuar.',
        tone: 'warning',
        showSpinner: false,
        showRefresh: false
      };
    case SIGNATURE_STATUS.SUPERSEDED:
      return {
        title: 'Este enlace fue reemplazado',
        description: 'Utilizá el enlace de firma más reciente para continuar.',
        tone: 'warning',
        showSpinner: false,
        showRefresh: false
      };
    case SIGNATURE_STATUS.NOT_SIGNABLE:
      return {
        title: 'No se puede completar la firma',
        description: apiMessage || DEFAULT_NOT_SIGNABLE_MESSAGE,
        tone: 'error',
        showSpinner: false,
        showRefresh: true
      };
    case SIGNATURE_STATUS.ERROR:
      return {
        title: 'No pudimos consultar el estado de la firma',
        description: 'Revisá tu conexión e intentá nuevamente.',
        tone: 'error',
        showSpinner: false,
        showRefresh: true
      };
    default:
      return null;
  }
};

const SignatureStatusCard = ({
  signatureStatus = SIGNATURE_STATUS.IDLE,
  signatureExpiresAt = '',
  lastSignatureStatusCheckedAt = '',
  signatureSignedAt = '',
  apiMessage = '',
  isRefreshing = false,
  canRefresh = false,
  onRefreshStatus
}) => {
  const content = useMemo(
    () => resolveStatusContent(signatureStatus, apiMessage),
    [apiMessage, signatureStatus]
  );

  if (!content || signatureStatus === SIGNATURE_STATUS.IDLE) {
    return null;
  }

  const expiresLabel = signatureExpiresAt ? formatDateTime(signatureExpiresAt) : '';
  const lastCheckedLabel = lastSignatureStatusCheckedAt ? formatDateTime(lastSignatureStatusCheckedAt) : '';
  const signedAtLabel = signatureSignedAt ? formatDateTime(signatureSignedAt) : '';
  const showRefreshAction = content.showRefresh && canRefresh && typeof onRefreshStatus === 'function';

  return (
    <section
      className={`rg-signature-status-card ${STATUS_TONE_CLASS[content.tone] || STATUS_TONE_CLASS.info}`}
      aria-label="Estado de la firma digital"
      aria-live="polite"
    >
      <div className="rg-signature-status-card__header">
        {content.showSpinner && (
          <span
            className="spinner-border spinner-border-sm rg-signature-status-card__spinner"
            aria-hidden="true"
          />
        )}
        <h6 className="rg-signature-status-card__title">{content.title}</h6>
      </div>

      {content.description && (
        <p className="rg-signature-status-card__description">{content.description}</p>
      )}

      {signatureStatus === SIGNATURE_STATUS.PENDING && (
        <dl className="rg-signature-status-card__meta">
          {expiresLabel && (
            <div className="rg-signature-status-card__meta-item">
              <dt>Vence</dt>
              <dd>{expiresLabel}</dd>
            </div>
          )}
          {lastCheckedLabel && (
            <div className="rg-signature-status-card__meta-item">
              <dt>Última actualización</dt>
              <dd>{lastCheckedLabel}</dd>
            </div>
          )}
        </dl>
      )}

      {signatureStatus === SIGNATURE_STATUS.COMPLETED && signedAtLabel && (
        <dl className="rg-signature-status-card__meta">
          <div className="rg-signature-status-card__meta-item">
            <dt>Firmado el</dt>
            <dd>{signedAtLabel}</dd>
          </div>
        </dl>
      )}

      {showRefreshAction && (
        <div className="rg-signature-status-card__actions">
          <Button
            variant="outlinePrimary"
            size="sm"
            onClick={onRefreshStatus}
            disabled={isRefreshing}
            aria-busy={isRefreshing}
          >
            {isRefreshing && (
              <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
            )}
            Actualizar estado
          </Button>
        </div>
      )}
    </section>
  );
};

export default SignatureStatusCard;
